import { useState } from "react";
import { AgentSdkThreadWaitTimer } from "@forgepoint/shared";
import { format, formatDistanceToNow } from "date-fns";
import { ChevronDown, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ThreadWaitTimerBarProps {
  timers: AgentSdkThreadWaitTimer[];
}

/**
 * Renders the non-cancelled wait timers for a thread as compact rows that
 * sit inside the follow-up strip above the reply composer. Purely
 * informational — the agent resumes automatically when a timer fires.
 */
export function ThreadWaitTimerBar({ timers }: ThreadWaitTimerBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  if (timers.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col divide-y border-t bg-info/10 animate-in fade-in slide-in-from-bottom-1 duration-200">
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((prev) => !prev)}
        className="px-6 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span className="mx-auto flex w-full max-w-3xl items-center gap-2">
          <ChevronDown
            className={cn(
              "h-3 w-3 transition-transform motion-reduce:transition-none",
              isExpanded && "rotate-180",
            )}
            aria-hidden
          />
          {timers.length === 1
            ? "1 wait timer"
            : `${timers.length} wait timers`}
        </span>
      </button>
      {isExpanded &&
        timers.map((timer) => (
          <ThreadWaitTimerRow key={timer.id} timer={timer} />
        ))}
    </div>
  );
}

interface ThreadWaitTimerRowProps {
  timer: AgentSdkThreadWaitTimer;
}

function ThreadWaitTimerRow({ timer }: ThreadWaitTimerRowProps) {
  const label = timer.subject ?? "Waiting";
  const relative = timer.waitUntil ? formatRelative(timer.waitUntil) : null;
  const absolute = timer.waitUntil ? formatAbsolute(timer.waitUntil) : null;
  return (
    <div className="px-6 py-2.5 text-xs text-muted-foreground">
      <div className="mx-auto flex w-full max-w-3xl items-center gap-3">
        <Clock
          className="h-3 w-3 shrink-0 text-info animate-pulse"
          aria-hidden
        />
        <div className="min-w-0 flex-1 truncate">
          <span className="font-medium text-foreground/80">{label}</span>
          {timer.description && (
            <span className="ml-2 text-muted-foreground">
              · {timer.description}
            </span>
          )}
        </div>
        {relative && absolute && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 tabular-nums">{relative}</span>
              </TooltipTrigger>
              <TooltipContent>{absolute}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

function formatRelative(target: Date): string {
  return formatDistanceToNow(target, { addSuffix: true });
}

function formatAbsolute(target: Date): string {
  return format(target, "PPp");
}
